import React, { useState } from 'react';
import { openCustomerPortal } from '../lib/billing';

// ─── TrialBanner ────────────────────────────────────────────────────────────
//
// Top-of-app banner shown when subscription_status is 'trialing' or when
// it's 'canceled' but the period hasn't elapsed yet. Two states:
//
//   trialing → "Free trial — N days remaining · Manage billing →"
//     Default tone is muted gray; escalates to amber at ≤3 days, red at
//     ≤1 day. The escalation is the kill-step that catches operators
//     about to hit a paywall and gives them a single click to resolve.
//
//   canceled with period_ends_at > now() → "Subscription canceled —
//     access until {date} · Manage billing →"  Always neutral tone.
//
// "Manage billing" opens the Stripe Customer Portal in the same tab; in
// the dev-mode case (no stripe_customer_id) the portal endpoint returns
// 'no_subscription_yet' and we route the operator into a fresh checkout
// instead — same as the BillingSettingsTab does.
//
// The banner is purely informational; it doesn't gate any other UI. The
// paywall handles the actual access cutoff.

const RED_THRESHOLD_DAYS = 1;
const AMBER_THRESHOLD_DAYS = 3;

export default function TrialBanner({ billing }) {
  const [busy, setBusy] = useState(false);
  if (!billing) return null;

  const status = billing.subscription_status;
  const daysRemaining = billing.trial_days_remaining;
  const periodEnd = billing.subscription_period_ends_at;

  // Decide whether to render at all + which copy to use.
  let mode = null;
  if (status === 'trialing') mode = 'trialing';
  else if (status === 'canceled' && periodEnd && new Date(periodEnd) > new Date()) mode = 'canceled-grace';
  else if (status === 'past_due') mode = 'past-due';
  if (!mode) return null;

  // Tone selection.
  let tone = 'neutral';
  if (mode === 'trialing') {
    if (daysRemaining != null && daysRemaining <= RED_THRESHOLD_DAYS) tone = 'red';
    else if (daysRemaining != null && daysRemaining <= AMBER_THRESHOLD_DAYS) tone = 'amber';
  }
  if (mode === 'past-due') tone = 'amber';

  const palette =
    tone === 'red' ? { bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5' } :
    tone === 'amber' ? { bg: '#FEF3C7', fg: '#92400E', border: '#FDE68A' } :
                       { bg: '#F3F4F6', fg: '#374151', border: '#E5E7EB' };

  let label;
  if (mode === 'trialing') {
    if (daysRemaining == null) label = 'Free trial active';
    else if (daysRemaining <= 0) label = 'Free trial — last day';
    else if (daysRemaining === 1) label = 'Free trial — 1 day remaining';
    else label = `Free trial — ${daysRemaining} days remaining`;
  } else if (mode === 'canceled-grace') {
    const d = new Date(periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    label = `Subscription canceled — access until ${d}`;
  } else if (mode === 'past-due') {
    label = 'Payment failed — update your card to keep access';
  }

  async function manage() {
    if (busy) return;
    setBusy(true);
    await openCustomerPortal({ onError: () => { /* swallow — settings page surfaces details */ } });
    setBusy(false);
  }

  return (
    <div
      className="no-print"
      style={{
        background: palette.bg,
        color: palette.fg,
        borderBottom: `1px solid ${palette.border}`,
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <span>{label}</span>
      <button
        type="button"
        onClick={manage}
        disabled={busy}
        style={{
          background: 'transparent',
          border: 'none',
          color: palette.fg,
          textDecoration: 'underline',
          fontWeight: 600,
          cursor: busy ? 'wait' : 'pointer',
          fontSize: 13,
          padding: 0,
        }}
      >
        Manage billing →
      </button>
    </div>
  );
}
